/* eslint-disable @typescript-eslint/no-var-requires */
import {applyMiddleware, combineReducers, compose, createStore} from 'redux';
import {thunk} from 'redux-thunk';

import {setupListeners} from '@reduxjs/toolkit/query';
import * as Sentry from '@sentry/react';

import accountStatementSlice from 'src/containers/AccountStatement/slice';
import agentClientsSlice from 'src/containers/AgentClients/slice';
import {agentCompanyClientsSlice} from 'src/containers/AgentCompanyClients';
import agentClientsPaymentsSlice from 'src/containers/AgentPayments/slice';
import appSlice from 'src/containers/App/slice';
import {bannersConfigSlice} from 'src/containers/BannersConfig';
import broadcastsSlice from 'src/containers/Broadcasts/slice';
import cascadesSlice from 'src/containers/Cascades/slice';
import channelsSlice from 'src/containers/Channels/slice';
import dialogsSlice from 'src/containers/Dialogs/slice';
import {leadClientsSlice} from 'src/containers/LeadCompanyClients';
import {messageMatcherPresetsSlice} from 'src/containers/MessageMatcherPresets';
import messageMatcherReportsSlice from 'src/containers/MessageMatcherReports/slice';
import whatsAppMessageMatcherSlice from 'src/containers/MessageMatchers/WhatsAppForm/slice';
import messageMatchersSlice from 'src/containers/MessageMatchers/slice';
import notificationsSlice from 'src/containers/Notifications/slice';
import notificationsHistorySlice from 'src/containers/NotificationsHistory/slice';
import parametersSlice from 'src/containers/Parameters/slice';
import reportSlice from 'src/containers/Report/slice';
import subjectsSlice from 'src/containers/Subjects/slice';
import subscribersSlice from 'src/containers/Subscribers/slice';
import summaryReportSlice from 'src/containers/SummaryReport/slice';
import supportClientsSlice from 'src/containers/SupportClients/slice';
import supportVerificationsSlice from 'src/containers/SupportVerifications/slice';
import {tariffsCatalogSlice} from 'src/containers/TariffsCatalog';
import triggersSlice from 'src/containers/Triggers/slice';
import {userSlice} from 'src/containers/User';
import {listenerMiddleware, rootApi} from 'src/models';
import {requestInterceptors} from 'src/models/requestInterceptors';

import {isEnvDevelopment} from './constants';

const composeWithDevTools = require('redux-devtools-extension').composeWithDevTools;

// https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/redux/
const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  actionTransformer: (action) => {
    const actionType = action.type;

    if (actionType.startsWith('auth')) {
      return {
        ...action,
        password: null,
        newPassword: null,
        otpId: null,
      };
    }

    if (actionType.startsWith('channel-profile/embedded-signup')) {
      return {
        ...action,
        token: null,
      };
    }

    return action;
  },
});

export const reducers = {
  [rootApi.reducerPath]: rootApi.reducer,
  app: appSlice,
  user: userSlice,
  bannersConfig: bannersConfigSlice,
  triggers: triggersSlice,
  channels: channelsSlice,
  notifications: notificationsSlice,
  notificationsHistory: notificationsHistorySlice,
  cascades: cascadesSlice,
  broadcasts: broadcastsSlice,
  report: reportSlice,
  summaryReport: summaryReportSlice,
  parameters: parametersSlice,
  accountStatement: accountStatementSlice,
  supportClients: supportClientsSlice,
  leadClients: leadClientsSlice,
  agentCompanyClients: agentCompanyClientsSlice,
  subscribers: subscribersSlice,
  subjects: subjectsSlice,
  messageMatchers: messageMatchersSlice,
  whatsAppMessageMatcher: whatsAppMessageMatcherSlice,
  messageMatcherPresets: messageMatcherPresetsSlice,
  messageMatcherReports: messageMatcherReportsSlice,
  supportVerifications: supportVerificationsSlice,
  agentClients: agentClientsSlice,
  agentClientsPayments: agentClientsPaymentsSlice,
  tariffsCatalog: tariffsCatalogSlice,
  dialogs: dialogsSlice,
};

const middlewares = [listenerMiddleware.middleware, thunk, rootApi.middleware];

const makeStore = ({extraReducers = {}} = {}) => {
  const store = isEnvDevelopment
    ? createStore(
        combineReducers({...reducers, ...extraReducers}),
        composeWithDevTools(applyMiddleware(...middlewares)),
      )
    : createStore(
        combineReducers({...reducers, ...extraReducers}),
        compose(applyMiddleware(...middlewares), sentryReduxEnhancer),
      );

  requestInterceptors(store);

  return store;
};

const store = makeStore();

// Инициализация слушателя событий фокуса и сети для параметров RTK, например skipPollingIfUnfocused
setupListeners(store.dispatch);

export {makeStore};

export default store;
/* eslint-enable @typescript-eslint/no-var-requires */
